// src/pages/RhDocuments.jsx — Documents RH (salariés)
import React, { useState, useEffect, useRef } from 'react';
import api, { mesDocumentsAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  Folders, FolderOpen, Eye, RefreshCw, Search, ChevronRight, ArrowLeft,
  FileText, Clock, CheckCircle2, XCircle, AlertCircle, Upload, X, FilePlus,
} from 'lucide-react';

// Même types que le modèle backend + MesDocuments.jsx
const DOC_TYPES = [
  { value: 'PIECE_IDENTITE',   label: "Pièce d'identité" },
  { value: 'SECURITE_SOCIALE', label: 'Sécurité sociale' },
  { value: 'CONTRAT_TRAVAIL',  label: 'Contrat de travail' },
  { value: 'AVENANT',          label: 'Avenant' },
  { value: 'RIB',              label: 'RIB' },
  { value: 'MUTUELLE',         label: 'Mutuelle' },
  { value: 'PERMIS_TRAVAIL',   label: 'Permis de travail' },
  { value: 'ATTESTATION',      label: 'Attestation' },
  { value: 'AUTRE',            label: 'Autre' },
];
const DOC_TYPE_MAP = Object.fromEntries(DOC_TYPES.map(t => [t.value, t.label]));

const DOC_STATUT_CFG = {
  SOUMIS:  { label: 'En attente', color: '#0891B2', icon: Clock },
  VALIDE:  { label: 'Validé',     color: '#059669', icon: CheckCircle2 },
  REJETE:  { label: 'Rejeté',     color: '#DC2626', icon: XCircle },
  ATTENTE: { label: 'En attente', color: '#F59E0B', icon: AlertCircle },
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

// ── Modale d'upload ───────────────────────────────────────────
function UploadModal({ userId, userName, onClose, onDone }) {
  const fileRef   = useRef(null);
  const [docType, setDocType]   = useState('CONTRAT_TRAVAIL');
  const [title,   setTitle]     = useState('');
  const [file,    setFile]      = useState(null);
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Sélectionnez un fichier'); return; }
    if (!title.trim()) { toast.error('Titre requis'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('document_type',  docType);
      fd.append('title',          title.trim());
      fd.append('commentaire',    '');
      fd.append('target_user_id', userId);
      fd.append('file',           file);
      await mesDocumentsAPI.upload(fd);
      toast.success('Document déposé');
      onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur upload');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 480, background: 'var(--bg-card)', borderRadius: 16,
        border: '1px solid var(--border)', padding: '28px 28px 24px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
              Déposer un document
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>
              Pour {userName}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
                                             color: 'var(--text-muted)', padding: 4 }}>
            <X size={18}/>
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Type */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600,
                            color: 'var(--text-secondary)', marginBottom: 6 }}>
              Type de document *
            </label>
            <select value={docType} onChange={e => setDocType(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 9,
                       border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                       color: 'var(--text-primary)', fontSize: '0.875rem' }}>
              {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {/* Titre */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600,
                            color: 'var(--text-secondary)', marginBottom: 6 }}>
              Titre *
            </label>
            <input
              type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Ex : Contrat CDI 2024"
              style={{ width: '100%', padding: '9px 12px', borderRadius: 9, boxSizing: 'border-box',
                       border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                       color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none' }}
            />
          </div>

          {/* Fichier */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600,
                            color: 'var(--text-secondary)', marginBottom: 6 }}>
              Fichier *
            </label>
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${file ? '#059669' : 'var(--border)'}`,
                borderRadius: 9, padding: '18px 16px', textAlign: 'center',
                cursor: 'pointer', transition: 'border-color 0.15s',
                background: file ? 'rgba(5,150,105,0.06)' : 'var(--bg-elevated)',
              }}
            >
              <Upload size={20} color={file ? '#059669' : 'var(--text-muted)'}
                style={{ display: 'block', margin: '0 auto 8px' }} />
              <div style={{ fontSize: '0.82rem', color: file ? '#059669' : 'var(--text-muted)', fontWeight: 600 }}>
                {file ? file.name : 'Cliquez pour choisir un fichier'}
              </div>
              {file && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  {(file.size / 1024).toFixed(0)} Ko
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" hidden
              onChange={e => setFile(e.target.files?.[0] || null)} />
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{
              flex: 1, padding: '10px', borderRadius: 9, border: '1px solid var(--border)',
              background: 'var(--bg-elevated)', cursor: 'pointer',
              color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.875rem',
            }}>
              Annuler
            </button>
            <button type="submit" disabled={loading} style={{
              flex: 2, padding: '10px', borderRadius: 9, border: 'none',
              background: loading ? 'var(--border)' : '#059669', cursor: loading ? 'default' : 'pointer',
              color: 'white', fontWeight: 700, fontSize: '0.875rem',
            }}>
              {loading ? 'Envoi…' : 'Déposer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── UserCard ──────────────────────────────────────────────────
function UserCard({ u, onSelect }) {
  const [hov, setHov] = useState(false);
  const pending = u.docs.filter(d => d.statut === 'SOUMIS' || d.statut === 'ATTENTE').length;
  const initls  = `${(u.prenom[0]||'')}${(u.nom[0]||'')}`.toUpperCase();
  return (
    <button
      onClick={() => onSelect(u)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 13,
        padding: '15px 16px', borderRadius: 13, cursor: 'pointer',
        border: `1.5px solid ${hov ? '#3B82F6' : 'var(--border)'}`,
        background: 'var(--bg-card)', textAlign: 'left',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: hov ? '0 6px 20px rgba(59,130,246,0.08)' : 'none',
        width: '100%',
      }}
    >
      <div style={{ width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(59,130,246,0.10)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '0.9rem', color: '#3B82F6' }}>
        {initls}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {u.prenom} {u.nom}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2,
                      display: 'flex', gap: 6, alignItems: 'center' }}>
          <FileText size={10}/> {u.docs.length} doc{u.docs.length !== 1 ? 's' : ''}
          {pending > 0 && (
            <span style={{ whiteSpace: 'nowrap', padding: '1px 7px', borderRadius: 10,
                           background: 'rgba(245,158,11,0.14)', color: '#D97706',
                           fontWeight: 700, fontSize: '0.67rem' }}>
              {pending} en attente
            </span>
          )}
        </div>
      </div>
      <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>
    </button>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function RhDocuments() {
  const [docs,          setDocs]          = useState([]);
  const [equipe,        setEquipe]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [selectedUser,  setSelectedUser]  = useState(null);
  const [typeFilter,    setTypeFilter]    = useState('');
  const [showUpload,    setShowUpload]    = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [docsRes, usersRes] = await Promise.all([
        mesDocumentsAPI.listAll(),
        api.get('/admin/equipe'),
      ]);
      setDocs(docsRes.data || []);
      setEquipe(usersRes.data || []);
    } catch {
      toast.error('Erreur chargement documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Construire la map : tous les utilisateurs, même sans docs
  const usersMap = {};
  equipe.forEach(u => {
    usersMap[u.id] = { key: u.id, id: u.id, nom: u.nom || '', prenom: u.prenom || '', docs: [] };
  });
  // Remplir les docs
  docs.forEach(d => {
    const key = d.user_id;
    if (!usersMap[key]) {
      usersMap[key] = { key, id: d.user_id, nom: d.user_nom || '', prenom: d.user_prenom || '', docs: [] };
    }
    usersMap[key].docs.push(d);
  });

  const allUsers = Object.values(usersMap).sort((a, b) =>
    `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`)
  );
  const filteredUsers = search
    ? allUsers.filter(u => `${u.prenom} ${u.nom}`.toLowerCase().includes(search.toLowerCase()))
    : allUsers;

  const totalDocs    = docs.length;
  const totalPending = docs.filter(d => d.statut === 'SOUMIS' || d.statut === 'ATTENTE').length;

  // ── Vue détail utilisateur ────────────────────────────────
  if (selectedUser) {
    const currentUserObj = usersMap[selectedUser.key] || selectedUser;
    const userDocs = currentUserObj.docs || [];
    const filtered = typeFilter ? userDocs.filter(d => d.document_type === typeFilter) : userDocs;
    const userName = `${selectedUser.prenom} ${selectedUser.nom}`;

    return (
      <div style={{ padding: '28px 36px', minHeight: '100vh' }}>
        {showUpload && (
          <UploadModal
            userId={selectedUser.id || selectedUser.key}
            userName={userName}
            onClose={() => setShowUpload(false)}
            onDone={() => {
              load();
              // Mettre à jour selectedUser après reload
              setTimeout(() => {
                setSelectedUser(prev => usersMap[prev?.key] || prev);
              }, 600);
            }}
          />
        )}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              onClick={() => setSelectedUser(null)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                       borderRadius: 9, border: '1px solid var(--border)',
                       background: 'var(--bg-elevated)', cursor: 'pointer',
                       color: 'var(--text-secondary)', fontSize: '0.82rem', fontWeight: 600 }}>
              <ArrowLeft size={14}/> Retour
            </button>
            <div style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                          background: 'rgba(59,130,246,0.12)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 800, fontSize: '0.9rem', color: '#3B82F6' }}>
              {`${(selectedUser.prenom[0]||'')}${(selectedUser.nom[0]||'')}`.toUpperCase()}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                {userName}
              </h2>
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {userDocs.length} document{userDocs.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)',
                       background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                       fontSize: '0.8rem', cursor: 'pointer' }}>
              <option value="">Tous les types</option>
              {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <button
              onClick={() => setShowUpload(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px',
                       borderRadius: 9, border: 'none', cursor: 'pointer',
                       background: '#059669', color: 'white', fontWeight: 700, fontSize: '0.83rem' }}>
              <FilePlus size={15}/> Déposer un document
            </button>
          </div>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
            <FolderOpen size={40} style={{ opacity: 0.2, display: 'block', margin: '0 auto 12px' }}/>
            Aucun document trouvé
          </div>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 14,
                        overflow: 'hidden', background: 'var(--bg-card)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                <thead>
                  <tr>
                    {['Type', 'Titre', 'Fichier', 'Statut', 'Déposé le', ''].map((h, i) => (
                      <th key={i} style={{
                        padding: '11px 16px', textAlign: 'left', fontWeight: 600,
                        color: 'var(--text-muted)', fontSize: '0.7rem',
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        background: 'var(--bg-elevated)',
                        borderBottom: '1px solid var(--border)',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d, i) => {
                    const sc  = DOC_STATUT_CFG[d.statut] || DOC_STATUT_CFG.ATTENTE;
                    const Ic  = sc.icon;
                    return (
                      <tr key={d.id} style={{ borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <td style={{ padding: '11px 16px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                          {DOC_TYPE_MAP[d.document_type] || d.document_type || '—'}
                        </td>
                        <td style={{ padding: '11px 16px', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {d.title || d.file_name || '—'}
                        </td>
                        <td style={{ padding: '11px 16px', color: 'var(--text-muted)', maxWidth: 200 }}>
                          <span title={d.file_name} style={{ display: 'block', overflow: 'hidden',
                                                             textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.file_name || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '11px 16px', whiteSpace: 'nowrap' }}>
                          <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4,
                                         padding: '3px 9px', borderRadius: 20,
                                         fontSize: '0.68rem', fontWeight: 700,
                                         background: `${sc.color}14`, color: sc.color }}>
                            <Ic size={10}/>{sc.label}
                          </span>
                        </td>
                        <td style={{ padding: '11px 16px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                          {fmtDate(d.created_at)}
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          {d.file_url && (
                            <a href={d.file_url} target="_blank" rel="noreferrer"
                               style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                        padding: '5px 11px', borderRadius: 7, textDecoration: 'none',
                                        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                                        color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 600 }}>
                              <Eye size={11}/> Voir
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Vue liste des utilisateurs ────────────────────────────────
  return (
    <div style={{ padding: '28px 36px', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(59,130,246,0.10)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Folders size={18} color="#3B82F6" />
            </div>
            <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)' }}>
              Dossiers salariés
            </h1>
          </div>
          <p style={{ margin: '0 0 0 46px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            {totalDocs} document{totalDocs !== 1 ? 's' : ''} · {allUsers.length} salarié{allUsers.length !== 1 ? 's' : ''}
            {totalPending > 0 && (
              <span style={{ whiteSpace: 'nowrap', marginLeft: 8, padding: '2px 8px', borderRadius: 12,
                             background: 'rgba(245,158,11,0.12)', color: '#D97706',
                             fontWeight: 700, fontSize: '0.72rem' }}>
                {totalPending} en attente
              </span>
            )}
          </p>
        </div>
        <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6,
                                        padding: '8px 14px', borderRadius: 9, border: '1px solid var(--border)',
                                        background: 'var(--bg-elevated)', cursor: 'pointer',
                                        color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          <RefreshCw size={13}/> Rafraîchir
        </button>
      </div>

      <div style={{ position: 'relative', marginBottom: 20, maxWidth: 360 }}>
        <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
                                   color: 'var(--text-muted)', pointerEvents: 'none' }}/>
        <input
          placeholder="Rechercher un salarié…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', paddingLeft: 32, paddingRight: 12, paddingTop: 9, paddingBottom: 9,
                   borderRadius: 9, border: '1px solid var(--border)', outline: 'none',
                   background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                   fontSize: '0.83rem', boxSizing: 'border-box' }}
        />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
          <div className="spinner" style={{ width: 32, height: 32 }}/>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>
          <FolderOpen size={44} style={{ opacity: 0.18, display: 'block', margin: '0 auto 14px' }}/>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Aucun salarié trouvé</div>
          <div style={{ fontSize: '0.8rem' }}>Aucun document déposé pour le moment</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
          {filteredUsers.map(u => (
            <UserCard key={u.key} u={u} onSelect={setSelectedUser} />
          ))}
        </div>
      )}
    </div>
  );
}
